import { Settings } from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { characterById, evidenceById } from '../case/case001';
import { AudioDirector } from '../audio/AudioDirector';
import { GameProvider, useGame } from '../game/GameContext';
import { renderGameToText } from '../game/textState';
import type { CharacterId, GameAction } from '../game/types';
import { AftermathScreen } from '../ui/AftermathScreen';
import { BriefingScreen } from '../ui/BriefingScreen';
import { DialogueOverlay } from '../ui/DialogueOverlay';
import { DossierOverlay } from '../ui/DossierOverlay';
import { EvidenceOverlay } from '../ui/EvidenceOverlay';
import { GameHUD } from '../ui/GameHUD';
import { RevealOverlay } from '../ui/RevealOverlay';
import { SettingsOverlay } from '../ui/SettingsOverlay';
import { TheoryScreen } from '../ui/TheoryScreen';
import { TitleScreen } from '../ui/TitleScreen';
import { CourtEventOverlay } from '../ui/CourtEventOverlay';
import { NoticeToast } from '../ui/NoticeToast';
import { ProfileOverlay } from '../ui/ProfileOverlay';

// Code-split from the Three.js/postprocessing dependency graph (the bulk of
// the bundle) so the 2D title screen can paint without waiting on it to
// parse. Safe to defer: the title art is fully opaque, so there is nothing
// to show under a null fallback while this chunk streams in behind it.
const GameWorld = lazy(() => import('../world/GameWorld').then((m) => ({ default: m.GameWorld })));

function GameShell() {
  const { state, dispatch } = useGame();
  const [nearby, setNearby] = useState<string | null>(null);
  const onNearby = useCallback((label: string | null) => setNearby(label), []);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.code === 'Tab' && ['investigation', 'trial'].includes(state.phase)) {
        event.preventDefault();
        dispatch({ type: 'OPEN_DOSSIER' });
      }
      if (event.code === 'KeyQ' && ['investigation', 'trial'].includes(state.phase)) {
        dispatch({ type: 'VIEW_EVIDENCE', evidenceId: state.discoveredEvidenceIds.at(-1) ?? 'charge-sheet' });
      }
      if (event.code === 'Escape' && state.overlay !== 'none') dispatch({ type: 'CLOSE_OVERLAY' });
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [dispatch, state.discoveredEvidenceIds, state.overlay, state.phase]);

  useEffect(() => {
    window.render_game_to_text = () => renderGameToText(state, nearby);
    window.advanceTime = (ms: number) => dispatch({ type: 'TICK', ms: Math.max(0, ms) });
    window.__HOTEI__ = {
      dispatch: (action: unknown) => dispatch(action as GameAction),
      interact: (id: string) => {
        if (evidenceById[id]) dispatch({ type: 'DISCOVER_EVIDENCE', evidenceId: id });
        else if (characterById[id as CharacterId]) dispatch({ type: 'START_DIALOGUE', characterId: id as CharacterId });
      },
      reset: () => dispatch({ type: 'RESET_CASE' }),
    };
  }, [dispatch, nearby, state]);

  const gameplay = state.phase === 'investigation' || state.phase === 'trial';
  return (
    <div className={`app phase-${state.phase}`}>
      <Suspense fallback={null}>
        <GameWorld onNearby={onNearby} />
      </Suspense>
      <AudioDirector />
      {state.phase === 'title' && <TitleScreen />}
      {state.phase === 'briefing' && <BriefingScreen />}
      {gameplay && <GameHUD nearby={nearby} />}
      {state.phase === 'theory' && <TheoryScreen />}
      {state.phase === 'reveal' && <RevealOverlay />}
      {state.phase === 'aftermath' && <AftermathScreen />}
      {state.overlay === 'evidence' && <EvidenceOverlay />}
      {state.overlay === 'dialogue' && <DialogueOverlay />}
      {state.overlay === 'dossier' && <DossierOverlay />}
      {state.overlay === 'settings' && <SettingsOverlay />}
      {state.overlay === 'profile' && <ProfileOverlay />}
      {state.courtEvent && <CourtEventOverlay />}
      <NoticeToast />
      {gameplay && state.overlay === 'none' && (
        <button className="settings-button hud-interactive" onClick={() => dispatch({ type: 'OPEN_SETTINGS' })} aria-label="Settings"><Settings /></button>
      )}
      <div className="film-grain" aria-hidden="true" />
    </div>
  );
}

export function App() {
  return <GameProvider><GameShell /></GameProvider>;
}
